import React, { useEffect, useState, useRef } from "react";
import { FaRegEdit, FaUserGraduate, FaDownload } from "react-icons/fa";
import { RiDeleteBin6Line, RiExchangeFundsLine } from "react-icons/ri";
import axios from "axios";
import * as XLSX from "xlsx";

import ClientWithdrawalStatusModal from "../../../components/Modals/ClientWithdrawalStatusModal";
import DeleteClientModal from "../../../components/Modals/DeleteClientModal";
import EditClientModal from "../../../components/Modals/EditClientModal";
import ClientDetailsModal from "../../../components/Modals/ClientDetailsModal";

const WithdrawalClients = ({
  query,
  page,
  itemsPerPage,
  onDataChange,
  onCertificateDataChange,
  dateFilter,
  certificateFilter,
}) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const previousDateFilter = useRef(null);
  const previousCertificateFilter = useRef(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setClients([]);
      let url = `${process.env.REACT_APP_API_URL}/our-client/get-clients`;

      if (certificateFilter) {
        const res = await axios.get(url);

        const certificateFilteredClients = res.data.filter((client) => {
          if (!client.servicesTaken || client.servicesTaken.length === 0)
            return false;
          return client.servicesTaken.some(
            (service) =>
              service.certificateId?.certificateName === "ISO 9001:2015" ||
              service.certificateId?.name === "ISO 9001:2015"
          );
        });

        const filteredClients = certificateFilteredClients.filter(
          (item) => item.status === "withdrawal"
        );

        setClients(filteredClients);
      } else if (dateFilter) {
        url = `${process.env.REACT_APP_API_URL}/our-client/filter/expiry?`;

        let fromDate, toDate;
        if (dateFilter.type === "single") {
          fromDate = `${dateFilter.year}-${dateFilter.month}-01`;
          toDate = new Date(dateFilter.year, parseInt(dateFilter.month), 0)
            .toISOString()
            .split("T")[0];
        } else if (dateFilter.type === "range") {
          fromDate = `${dateFilter.startYear}-${dateFilter.startMonth}-01`;
          toDate = new Date(
            dateFilter.endYear,
            parseInt(dateFilter.endMonth),
            0
          )
            .toISOString()
            .split("T")[0];
        }
        url += `from=${fromDate}&to=${toDate}`;

        const res = await axios.get(url);
        const filteredClients = res.data.filter(
          (item) => item.status === "withdrawal"
        );
        setClients(filteredClients);
      } else {
        const res = await axios.get(url);
        const filteredClients = res.data.filter(
          (item) => item.status === "withdrawal"
        );
        setClients(filteredClients);
      }
    } catch (err) {
      console.error("Error fetching withdrawal clients:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = () => {
    if (filteredData.length === 0) {
      alert("No data to export");
      return;
    }

    const exportData = [];

    filteredData.forEach((client) => {
      if (client.servicesTaken && client.servicesTaken.length > 0) {
        client.servicesTaken.forEach((service) => {
          exportData.push({
            "Client Name": client.clientName || client.name || "",
            Phone: client.phone || client.phoneNumber || client.mobile || "",
            Email: client.email || "",
            "Service Taken":
              service.certificateId?.certificateName ||
              service.certificateId?.name ||
              "",
            Status: client.status || "",
            "Expiry Date": service.expiryDate
              ? new Date(service.expiryDate).toLocaleDateString()
              : "",
          });
        });
      } else {
        exportData.push({
          "Client Name": client.clientName || client.name || "",
          Phone: client.phone || client.phoneNumber || client.mobile || "",
          Email: client.email || "",
          "Service Taken": "",
          Status: client.status || "",
          "Expiry Date": "",
        });
      }
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Withdrawal Clients");

    const colWidths = [
      { wch: 20 },
      { wch: 15 },
      { wch: 25 },
      { wch: 20 },
      { wch: 10 },
      { wch: 15 },
    ];
    ws["!cols"] = colWidths;

    const today = new Date().toISOString().split("T")[0];
    const filename = `Withdrawal_Clients_${today}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  useEffect(() => {
    const dateFilterChanged =
      JSON.stringify(dateFilter) !== JSON.stringify(previousDateFilter.current);
    const certificateFilterChanged =
      JSON.stringify(certificateFilter) !==
      JSON.stringify(previousCertificateFilter.current);

    if (dateFilterChanged || certificateFilterChanged) {
      previousDateFilter.current = dateFilter;
      previousCertificateFilter.current = certificateFilter;
      fetchData();
    }
  }, [dateFilter, certificateFilter]);

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = React.useMemo(() => {
    return clients.filter((item) =>
      (item?.clientName || item?.name || "")
        .toLowerCase()
        .includes((query || "").toLowerCase())
    );
  }, [clients, query]);

  const { paginatedClients, totalPages } = React.useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = filteredData.slice(startIndex, endIndex);
    const total = Math.ceil(filteredData.length / itemsPerPage);
    return { paginatedClients: paginated, totalPages: total };
  }, [filteredData, page, itemsPerPage]);

  useEffect(() => {
    if (!loading && clients.length >= 0) {
      onDataChange(totalPages, filteredData.length, filteredData);
      if (certificateFilter && onCertificateDataChange) {
        onCertificateDataChange(filteredData);
      }
    }
  }, [totalPages, filteredData.length, loading, certificateFilter]);

  const handleEditClick = (client) => {
    setSelectedClient(client);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (client) => {
    setSelectedClient(client);
    setDeleteModalOpen(true);
  };

  const handleStatusClick = (client) => {
    setSelectedClient(client);
    setStatusModalOpen(true);
  };

  const handleDetailsClick = (client) => {
    setSelectedClient(client);
    setDetailsModalOpen(true);
  };

  const closeModals = () => {
    setStatusModalOpen(false);
    setEditModalOpen(false);
    setDeleteModalOpen(false);
    setDetailsModalOpen(false);
    setSelectedClient(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedClient?._id) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/our-client/delete-client/${selectedClient._id}`,
        {
          headers: { "Content-Type": "application/json" },
          data: { email: selectedClient.email || "" },
        }
      );
      closeModals();
      fetchData();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete client.");
    }
  };

  return (
    <section className="px-2 mb-4">
      <div className="flex justify-end mb-4">
        <button
          onClick={handleExportToExcel}
          disabled={loading || filteredData.length === 0}
          className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg flex items-center gap-2 transition-colors duration-200"
        >
          <FaDownload className="w-4 h-4" />
          Export to Excel
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 py-2">
          {Array.from({ length: itemsPerPage }).map((_, idx) => (
            <div
              key={idx}
              className="bg-white p-5 rounded-lg shadow-lg flex flex-col items-center text-center"
            >
              <div className="animate-pulse w-20 h-20 rounded-full bg-gray-300 mb-4"></div>
              <div className="animate-pulse w-32 h-4 bg-gray-300 mb-2"></div>
              <div className="animate-pulse w-40 h-3 bg-gray-200 mb-4"></div>
              <div className="w-full flex justify-between gap-4 px-4">
                <div className="w-full h-8 bg-gray-300 rounded animate-pulse"></div>
                <div className="w-8 h-8 bg-gray-300 rounded animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
      ) : paginatedClients.length === 0 ? (
        <div className="flex justify-center">
          <p className="p-4 text-gray-500">No withdrawal clients found.</p>
        </div>
      ) : (
        <div className="overflow-x-auto sm:overflow-visible">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 py-2">
            {paginatedClients.map((client, idx) => (
              <div
                key={client._id || idx}
                className="relative bg-white p-5 rounded-lg shadow-lg flex flex-col items-center text-center border-2"
              >
                <div className="absolute top-2 right-2 flex gap-2">
                  <button
                    className="text-blue-600"
                    onClick={() => handleEditClick(client)}
                    aria-label="Edit client"
                  >
                    <FaRegEdit className="h-5 w-5" />
                  </button>
                  <button
                    className="text-red-600"
                    onClick={() => handleDeleteClick(client)}
                    aria-label="Delete client"
                  >
                    <RiDeleteBin6Line className="h-5 w-5" />
                  </button>
                </div>

                <p className="absolute top-2 left-2 bg-gradient-to-r from-[#0070a8] to-[#023955] rounded-2xl text-white text-xs font-normal tracking-wide py-1 px-2">
                  Withdrawal
                </p>

                <FaUserGraduate className="w-20 h-20 rounded-full object-cover mb-4 border-2 border-white shadow-md text-[#1B81B2]" />
                <h3 className="font-semibold text-gray-900">
                  {client.clientName || client.name}
                </h3>
                <p className="text-sm text-blue-600 mt-1 bg-blue-50 px-3 py-1 rounded-full">
                  {client.email}
                </p>

                <div className="w-full flex justify-between gap-4 px-4">
                  <div className="w-full">
                    <button
                      type="button"
                      onClick={() => handleDetailsClick(client)}
                      className="mt-6 w-full bg-gradient-to-r from-[#0070a8] to-[#023955] hover:from-[#1e8cbf] hover:to-[#1674a3] text-white tracking-wide font-medium py-1.5 rounded-lg text-sm shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      Details
                    </button>
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => handleStatusClick(client)}
                      className="mt-6 w-full bg-gradient-to-r from-[#0070a8] to-[#023955] hover:from-[#1e8cbf] hover:to-[#1674a3] text-white tracking-wide font-medium px-1.5 py-1.5 rounded-lg text-sm md:text-base shadow-md hover:shadow-lg transition-all duration-300"
                      aria-label="Change client status"
                    >
                      <RiExchangeFundsLine size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {statusModalOpen && selectedClient && (
        <ClientWithdrawalStatusModal
          client={selectedClient}
          onClose={closeModals}
        />
      )}
      {deleteModalOpen && selectedClient && (
        <DeleteClientModal
          itemName={selectedClient.clientName || selectedClient.name}
          onClose={closeModals}
          onConfirm={handleDeleteConfirm}
        />
      )}
      {editModalOpen && selectedClient && (
        <EditClientModal
          client={selectedClient}
          onClose={closeModals}
          onSubmit={closeModals}
        />
      )}
      {detailsModalOpen && selectedClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 overflow-y-auto">
          <div className="max-w-full w-full sm:max-w-lg">
            <ClientDetailsModal client={selectedClient} onClose={closeModals} />
          </div>
        </div>
      )}
    </section>
  );
};

export default WithdrawalClients;
